import { useEffect, useMemo, useState } from "react";
import { ArrowRight, CalendarRange, History as HistoryIcon, LoaderCircle, Trash2 } from "lucide-react";
import { api } from "../api/client";
import { LoadingBlock } from "../components/ui";
import { addDays, todayInJapan } from "../domain/form";
import { yen } from "../../shared/calculations";
import type { HistoryItem } from "../../shared/domain";

export function HistoryPage({ onOpenDate }: { onOpenDate: (date: string) => void }) {
  const today = useMemo(todayInJapan, []);
  const [from, setFrom] = useState(addDays(today, -30));
  const [to, setTo] = useState(today);
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [deletingDate, setDeletingDate] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    setMessage("");
    api.getHistory(from, to).then(setItems).catch((reason: Error) => setError(reason.message)).finally(() => setLoading(false));
  };

  useEffect(load, []); // 初期範囲だけを自動取得

  const deleteDate = async (date: string) => {
    const firstConfirmation = window.confirm(
      `${date} の売上・経費・勤務・未収を営業日ごと削除します。\n\n月次集計から除外されます。監査履歴は復元のため残ります。続けますか？`,
    );
    if (!firstConfirmation) return;
    const typedDate = window.prompt(`最終確認です。削除する日付「${date}」を入力してください。`);
    if (typedDate === null) return;
    if (typedDate.trim() !== date) {
      setError("日付が一致しなかったため削除しませんでした");
      return;
    }
    setDeletingDate(date);
    setError("");
    setMessage("");
    try {
      await api.deleteDaily(date);
      setItems((current) => current.filter((item) => item.businessDate !== date));
      setMessage(`${date} を削除し、月次集計から除外しました`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "営業日を削除できませんでした");
    } finally {
      setDeletingDate("");
    }
  };

  return (
    <div className="page-stack">
      <header className="page-heading">
        <div>
          <span className="eyebrow"><HistoryIcon size={15} /> 保存履歴</span>
          <h1>営業日の履歴</h1>
          <p>保存済みの現在版を確認し、選択した営業日を再編集できます。</p>
        </div>
      </header>

      <section className="filter-bar" aria-label="期間指定">
        <label className="field">
          <span className="field__label">開始日</span>
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <span className="filter-separator">〜</span>
        <label className="field">
          <span className="field__label">終了日</span>
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
        <button type="button" className="button button--primary" onClick={load}>
          <CalendarRange size={17} /> 表示
        </button>
      </section>

      {error ? <div className="inline-alert inline-alert--error" role="alert">{error}</div> : null}
      {message ? <div className="inline-alert inline-alert--success" role="status">{message}</div> : null}
      {loading ? <LoadingBlock /> : items.length === 0 ? (
        <div className="empty-state">
          <HistoryIcon size={28} aria-hidden="true" />
          <h2>この期間の保存データはありません</h2>
          <p>日次入力から営業日を保存すると、ここに表示されます。</p>
        </div>
      ) : (
        <div className="history-list">
          {items.map((item) => (
            <div className="history-row" key={item.businessDate}>
              <button type="button" className="history-row__open" onClick={() => onOpenDate(item.businessDate)}>
                <div className="history-row__date">
                  <strong>{item.businessDate.replaceAll("-", ".")}</strong>
                  <span>rev.{item.revision} · {new Date(item.updatedAt).toLocaleString("ja-JP")}</span>
                </div>
                <dl className="history-row__metrics">
                  <div><dt>売上</dt><dd>{yen(item.salesTotal)}</dd></div>
                  <div><dt>利益</dt><dd className={item.dailyProfit < 0 ? "is-negative" : ""}>{yen(item.dailyProfit)}</dd></div>
                  <div><dt>レジ差額</dt><dd className={item.registerDifference != null && item.registerDifference !== 0 ? "is-warning" : ""}>{item.registerDifference == null ? "未確認" : yen(item.registerDifference)}</dd></div>
                </dl>
                <div className="history-row__editor">
                  <span>{item.updatedBy}</span>
                  <ArrowRight size={18} aria-hidden="true" />
                </div>
              </button>
              <button
                type="button"
                className="history-row__delete"
                onClick={() => deleteDate(item.businessDate)}
                disabled={Boolean(deletingDate)}
                aria-label={`${item.businessDate}を削除`}
                title="営業日を削除"
              >
                {deletingDate === item.businessDate ? <LoaderCircle className="spin" size={17} /> : <Trash2 size={17} />}
                <span>{deletingDate === item.businessDate ? "削除中" : "削除"}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
